import { useState } from 'react';
import { useNotification } from '../../contexts/NotificationContext';
import { useBadges } from '../../contexts/BadgeContext';
import { badgeService } from '../../services/badgeService';
import { Upload, X } from 'lucide-react';
import { StudentSearch } from './StudentSearch';
interface SelectedStudent {
  userId: string;
  name: string;
  grade?: string | number;
}

export const BulkBadgeAward = () => {
  const [selectedStudents, setSelectedStudents] = useState<SelectedStudent[]>([]);
  const [awards, setAwards] = useState([{ badgeId: '', xpAmount: 10 }]);
  const [pumaPoints, setPumaPoints] = useState(0);
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  
  const { addNotification } = useNotification();
  const { badges: badgeDefinitions } = useBadges();

  const handleAwardChange = (index: number, field: string, value: any) => {
    setAwards(prev => prev.map((a, i) => i === index ? { ...a, [field]: value } : a));
  };

  const addAwardRow = () => setAwards(prev => [...prev, { badgeId: '', xpAmount: 10 }]);

  const removeAwardRow = (index: number) => setAwards(prev => prev.filter((_, i) => i !== index));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedStudents.length === 0) {
      addNotification('Please add at least one student', 'error');
      return;
    }

    if (awards.length === 0) {
      addNotification('Please add at least one badge award', 'error');
      return;
    }

    for (const a of awards) {
      if (!a.badgeId) {
        addNotification('Please select a badge for each award', 'error');
        return;
      }
      if (a.xpAmount <= 0) {
        addNotification('XP amount must be greater than 0', 'error');
        return;
      }
    }

    if (!reason.trim()) {
      addNotification('Please provide a reason for awarding XP', 'error');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const bulkRequest = {
        pumaPoints,
        awardXPRequests: [] as {
          studentId: string;
          badgeId: string;
          xpAmount: number;
          reason: string;
          pumaPoints?: number;
        }[]
      };

      for (const student of selectedStudents) {
        for (const award of awards) {
          bulkRequest.awardXPRequests.push({
            studentId: student.userId,
            badgeId: award.badgeId,
            xpAmount: award.xpAmount,
            reason,
            pumaPoints
          });
        }
      }

      const nameMap: Record<string, string> = {};
      selectedStudents.forEach(s => {
        nameMap[s.userId] = s.name;
      });

      const badgeMap: Record<string, string> = {};
      badgeDefinitions.forEach(b => {
        badgeMap[b.id] = b.name;
      });

      const res = await badgeService.awardBulkXP(bulkRequest);
      const displayResults = (res || []).map((r: any) => ({
        ...r,
        studentName: nameMap[r.StudentId ?? r.studentId] || nameMap[r.studentId] || r.studentId,
        badgeName: badgeMap[r.BadgeId ?? r.badgeId] || badgeMap[r.badgeId] || r.badgeId
      }));
      setResults(displayResults);

      addNotification(`Awards processed for ${selectedStudents.length} student(s)`, 'success');

      setSelectedStudents([]);
      setAwards([{ badgeId: '', xpAmount: 10 }]);
      setPumaPoints(0);
      setReason('');
    } catch (error) {
      addNotification('Failed to award XP to some students', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Bulk Award Badge XP</h2>
      <p className="text-gray-600 mb-6">
        Award XP to multiple students for specific badges
      </p>
      
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Search and Add Students
        </label>
        <StudentSearch
          onStudentSelect={(student) => {
            if (!selectedStudents.some(s => s.userId === student.userId)) {
              setSelectedStudents(prev => [...prev, student]);
            }
          }}
        />
        {selectedStudents.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {selectedStudents.map(s => (
              <span key={s.userId} className="flex items-center bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full">
                {s.name}
                <button type="button" className="ml-1" onClick={() => setSelectedStudents(prev => prev.filter(st => st.userId !== s.userId))}>
                  <X className="w-4 h-4" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">

        {awards.map((award, index) => (
          <div key={index} className="relative border rounded-lg p-4 space-y-4">
            <button type="button" className="absolute top-1 right-1 text-gray-400 hover:text-red-600" onClick={() => removeAwardRow(index)}>
              <X className="w-4 h-4" />
            </button>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Select Badge</label>
              <select
                value={award.badgeId}
                onChange={(e) => handleAwardChange(index, 'badgeId', e.target.value)}
                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                disabled={isSubmitting}
              >
                <option value="">Select a badge...</option>
                {badgeDefinitions.map((badge) => (
                  <option key={badge.id} value={badge.id}>
                    {badge.name} ({badge.category})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">XP Amount</label>
              <input
                type="number"
                min="1"
                value={award.xpAmount}
                onChange={(e) => handleAwardChange(index, 'xpAmount', Number(e.target.value))}
                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                disabled={isSubmitting}
              />
            </div>
          </div>
        ))}

        <button type="button" onClick={addAwardRow} className="text-indigo-600 hover:underline text-sm">
          Add Another Badge
        </button>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Puma Points (optional)</label>
          <input
            type="number"
            min="0"
            value={pumaPoints}
            onChange={(e) => setPumaPoints(Number(e.target.value))}
            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
            disabled={isSubmitting}
          />
        </div>
        
        <div>
          <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-1">
            Reason for XP Award
          </label>
          <input
            type="text"
            id="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g., Outstanding performance in math"
            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
            disabled={isSubmitting}
          />
        </div>
        
        <div>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
              isSubmitting
                ? 'bg-indigo-400 cursor-not-allowed'
                : 'bg-indigo-600 hover:bg-indigo-700'
            }`}
          >
            <Upload size={16} className="mr-2" />
            {isSubmitting ? 'Processing...' : 'Award XP'}
          </button>
        </div>
      </form>

      {results.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-medium text-gray-800 mb-2">Results</h3>
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Student</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Badge</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">New XP</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Current Level</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {results.map((r, idx) => (
                <tr key={idx}>
                  <td className="px-3 py-2 whitespace-nowrap">{r.studentName}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.badgeName}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.NewXP ?? r.newXP}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.CurrentLevel ?? r.currentLevel ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};